/**
 * Route Builder - Generate React Router routes for all item types.
 *
 * Uses a single catch-all route `/:resource` that handles any Aras ItemType.
 * Generic pages auto-detect the resource from URL params and create
 * runtime configs via createDefaultConfig().
 */
import React, { lazy, Suspense } from "react";
import { Route } from "react-router-dom";
import { Skeleton } from "antd";

// Lazy load generic pages
const GenericList = lazy(() =>
  import("../pages/generic/GenericList").then((m) => ({
    default: m.GenericList,
  })),
);
const GenericShow = lazy(() =>
  import("../pages/generic/GenericShow").then((m) => ({
    default: m.GenericShow,
  })),
);
const GenericCreate = lazy(() =>
  import("../pages/generic/GenericCreate").then((m) => ({
    default: m.GenericCreate,
  })),
);
const GenericEdit = lazy(() =>
  import("../pages/generic/GenericEdit").then((m) => ({
    default: m.GenericEdit,
  })),
);

const LoadingFallback = () => (
  <div style={{ padding: 24 }}>
    <Skeleton active paragraph={{ rows: 10 }} />
  </div>
);

/**
 * Generate catch-all entity routes.
 * Any path like /SomeItemType will be handled by generic pages.
 */
export function buildEntityRoutes(): React.ReactNode[] {
  const dynamicRoute = (
    <Route key="dynamic-itemtype" path="/:resource">
      <Route
        index
        element={
          <Suspense fallback={<LoadingFallback />}>
            <GenericList />
          </Suspense>
        }
      />
      <Route
        path="create"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <GenericCreate />
          </Suspense>
        }
      />
      <Route
        path=":id"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <GenericShow />
          </Suspense>
        }
      />
      <Route
        path=":id/edit"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <GenericEdit />
          </Suspense>
        }
      />
    </Route>
  );

  return [dynamicRoute];
}
